import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X, Loader2 } from 'lucide-react';
import { useLeaders, useContactCategories } from '../hooks/useContacts';
import { TagInput } from './TagInput';
import { LocationPicker } from './LocationPicker';
import {
  GENDER_LABELS,
  STATE_CODES,
  type Contact,
  type ContactInsert,
} from '../types';

// ─── Máscaras de input ────────────────────────────────────────────────────────
function maskPhone(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 11);
  if (d.length === 0) return '';
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

function maskCEP(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 8);
  if (d.length > 5) return `${d.slice(0, 5)}-${d.slice(5)}`;
  return d;
}

// ─── Schema Zod ───────────────────────────────────────────────────────────────
const schema = z.object({
  full_name:          z.string().min(2, 'Nome deve ter ao menos 2 caracteres'),
  category:           z.string().min(1, 'Selecione uma categoria'),
  phone:              z.string().optional().nullable(),
  phone_whatsapp:     z.boolean(),
  email:              z.string().email('E-mail inválido').optional().nullable().or(z.literal('')),
  birth_date:         z.string().optional().nullable(),
  gender:             z.enum(['M','F','outro','prefiro_nao_informar']).optional().nullable(),
  cpf:                z.string().optional().nullable(),
  neighborhood:       z.string().optional().nullable(),
  city:               z.string().optional().nullable(),
  state_code:         z.string().optional().nullable(),
  zip_code:           z.string().optional().nullable(),
  street:             z.string().optional().nullable(),
  street_number:      z.string().optional().nullable(),
  electoral_zone:     z.string().optional().nullable(),
  electoral_section:  z.string().optional().nullable(),
  voter_registration: z.string().optional().nullable(),
  // leader_id: string UUID, string vazia ou null — convertido em null no onValid
  leader_id:          z.preprocess(
    (val) => (val === '' ? null : val),
    z.string().uuid().nullable().optional(),
  ),
  tags:               z.array(z.string()).default([]),
  notes:              z.string().optional().nullable(),
  engagement_score:   z.coerce.number().min(0).max(100),
  latitude:           z.number().nullable().optional(),
  longitude:          z.number().nullable().optional(),
});

type FormValues = z.infer<typeof schema>;

interface ContactFormProps {
  contact?: Contact | null;
  onSubmit: (data: ContactInsert) => void;
  onClose: () => void;
  isPending: boolean;
}

export function ContactForm({ contact, onSubmit, onClose, isPending }: ContactFormProps) {
  const { data: leaders = [] } = useLeaders();
  const { data: categories = [] } = useContactCategories();

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      full_name:          '',
      category:           '',
      phone:              '',
      phone_whatsapp:     true,
      email:              '',
      birth_date:         '',
      gender:             null,
      cpf:                '',
      neighborhood:       '',
      city:               '',
      state_code:         null,
      zip_code:           '',
      street:             '',
      street_number:      '',
      electoral_zone:     '',
      electoral_section:  '',
      voter_registration: '',
      leader_id:          null,
      tags:               [],
      notes:              '',
      engagement_score:   0,
      latitude:           null,
      longitude:          null,
    },
  });

  /* Quando categories carregam e não há categoria selecionada, pré-seleciona a primeira */
  useEffect(() => {
    if (categories.length > 0 && !contact) {
      const current = watch('category');
      if (!current) setValue('category', categories[0].slug);
    }
  }, [categories, contact, watch, setValue]);

  /* Preenche ao editar */
  useEffect(() => {
    if (contact) {
      reset({
        full_name:          contact.full_name,
        category:           contact.category,
        phone:              contact.phone ?? '',
        phone_whatsapp:     contact.phone_whatsapp,
        email:              contact.email ?? '',
        birth_date:         contact.birth_date ?? '',
        gender:             contact.gender ?? null,
        cpf:                contact.cpf ?? '',
        neighborhood:       contact.neighborhood ?? '',
        city:               contact.city ?? '',
        state_code:         contact.state_code ?? null,
        zip_code:           contact.zip_code ?? '',
        street:             contact.street ?? '',
        street_number:      contact.street_number ?? '',
        electoral_zone:     contact.electoral_zone ?? '',
        electoral_section:  contact.electoral_section ?? '',
        voter_registration: contact.voter_registration ?? '',
        leader_id:          contact.leader_id ?? null,
        tags:               contact.tags ?? [],
        notes:              contact.notes ?? '',
        engagement_score:   contact.engagement_score,
        latitude:           contact.latitude ?? null,
        longitude:          contact.longitude ?? null,
      });
    }
  }, [contact, reset]);

  const onValid = (values: FormValues) => {
    const clean: ContactInsert = {
      ...values,
      phone:              values.phone || null,
      email:              values.email || null,
      birth_date:         values.birth_date || null,
      cpf:                values.cpf || null,
      neighborhood:       values.neighborhood || null,
      city:               values.city || null,
      state_code:         values.state_code || null,
      zip_code:           values.zip_code || null,
      street:             values.street || null,
      street_number:      values.street_number || null,
      electoral_zone:     values.electoral_zone || null,
      electoral_section:  values.electoral_section || null,
      voter_registration: values.voter_registration || null,
      leader_id:          values.leader_id ?? null,
      tags:               values.tags ?? [],
      latitude:           values.latitude ?? null,
      longitude:          values.longitude ?? null,
    };
    onSubmit(clean);
  };

  /* Hint de endereço para geocodificação */
  const [watchNeighborhood, watchCity, watchState] = watch(['neighborhood', 'city', 'state_code']);
  const addressHint = [watchNeighborhood, watchCity, watchState].filter(Boolean).join(', ') || undefined;

  /* Valores atuais de lat/lng para o LocationPicker */
  const [watchLat, watchLng] = watch(['latitude', 'longitude']);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal — flex column com altura máxima */}
      <div className="relative z-10 w-full max-w-2xl flex flex-col rounded-2xl glass-strong shadow-deep animate-fade-in max-h-[calc(100vh-2rem)]">

          {/* Header — fixo no topo */}
          <div className="flex items-center justify-between px-6 py-4 glass-strong hairline-b shrink-0 rounded-t-2xl">
            <div>
              <h2 className="font-display text-lg font-bold text-ink">
                {contact ? 'Editar Contato' : 'Novo Contato'}
              </h2>
              <p className="text-xs text-ink-muted mt-0.5">
                {contact ? `Editando ${contact.full_name}` : 'Preencha os dados do novo contato.'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="rounded-xl p-2 text-ink-muted hover:bg-white/5 hover:text-ink transition-colors cursor-pointer"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Corpo — scrollável */}
          <div className="overflow-y-auto flex-1">
        <form id="contact-form" onSubmit={handleSubmit(onValid)} className="px-6 py-6 space-y-6">
          {/* ── Seção 1 — Dados básicos ── */}
          <FormSection title="Dados básicos">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FieldGroup label="Nome completo *" error={errors.full_name?.message} className="sm:col-span-2">
                <input {...register('full_name')} placeholder="Ana Silva" className={inputCls(!!errors.full_name)} />
              </FieldGroup>

              {/* Categorias dinâmicas do tenant */}
              <FieldGroup label="Categoria *" error={errors.category?.message}>
                <select {...register('category')} className={inputCls(!!errors.category)}>
                  {categories.length === 0 && (
                    <option value="">Carregando…</option>
                  )}
                  {categories.map((c) => (
                    <option key={c.id} value={c.slug}>{c.label}</option>
                  ))}
                </select>
              </FieldGroup>

              {/* Liderança — campo opcional, sem validação UUID no form */}
              <FieldGroup label="Liderança responsável">
                <select {...register('leader_id')} className={inputCls(false)}>
                  <option value="">— Sem liderança —</option>
                  {leaders.map((l) => (
                    <option key={l.id} value={l.id}>{l.full_name}</option>
                  ))}
                </select>
              </FieldGroup>

              {/* Telefone com máscara */}
              <FieldGroup label="Telefone" error={errors.phone?.message}>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(maskPhone(e.target.value))}
                      placeholder="(82) 99999-9999"
                      inputMode="tel"
                      className={inputCls(!!errors.phone)}
                    />
                  )}
                />
              </FieldGroup>

              <FieldGroup label="E-mail" error={errors.email?.message}>
                <input {...register('email')} type="email" placeholder="ana@exemplo.com" className={inputCls(!!errors.email)} />
              </FieldGroup>

              <FieldGroup label="Data de nascimento">
                <input {...register('birth_date')} type="date" className={inputCls(false)} />
              </FieldGroup>

              <FieldGroup label="Gênero">
                <select {...register('gender')} className={inputCls(false)}>
                  <option value="">— Não informado —</option>
                  {Object.entries(GENDER_LABELS).map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </FieldGroup>
            </div>

            <label className="flex items-center gap-2 text-sm text-ink-muted cursor-pointer">
              <input {...register('phone_whatsapp')} type="checkbox" className="h-4 w-4 rounded accent-accent" />
              <span>Aceita WhatsApp neste número</span>
            </label>
          </FormSection>

          {/* ── Seção 2 — Tags ── */}
          <FormSection title="Tags">
            <Controller
              name="tags"
              control={control}
              render={({ field }) => (
                <TagInput value={field.value ?? []} onChange={field.onChange} />
              )}
            />
            <p className="text-[11px] text-ink-subtle mt-1">
              Digite para buscar ou criar novas tags. Pressione Enter para confirmar.
            </p>
          </FormSection>

          {/* ── Seção 3 — Endereço ── */}
          <FormSection title="Endereço">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <FieldGroup label="Bairro" className="sm:col-span-2">
                <input {...register('neighborhood')} placeholder="Centro" className={inputCls(false)} />
              </FieldGroup>

              {/* CEP com máscara */}
              <FieldGroup label="CEP">
                <Controller
                  name="zip_code"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(maskCEP(e.target.value))}
                      placeholder="57000-000"
                      inputMode="numeric"
                      className={inputCls(false)}
                    />
                  )}
                />
              </FieldGroup>

              <FieldGroup label="Rua" className="sm:col-span-2">
                <input {...register('street')} placeholder="Rua das Flores" className={inputCls(false)} />
              </FieldGroup>
              <FieldGroup label="Número">
                <input {...register('street_number')} placeholder="123" className={inputCls(false)} />
              </FieldGroup>
              <FieldGroup label="Cidade" className="sm:col-span-2">
                <input {...register('city')} placeholder="Maceió" className={inputCls(false)} />
              </FieldGroup>
              <FieldGroup label="UF">
                <select {...register('state_code')} className={inputCls(false)}>
                  <option value="">—</option>
                  {STATE_CODES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </FieldGroup>
            </div>
          </FormSection>

          {/* ── Seção 4 — Localização ── */}
          <FormSection title="Localização geográfica">
            <Controller
              name="latitude"
              control={control}
              render={() => (
                <LocationPicker
                  latitude={watchLat ?? null}
                  longitude={watchLng ?? null}
                  addressHint={addressHint}
                  onChange={(lat, lng) => {
                    setValue('latitude',  lat,  { shouldDirty: true });
                    setValue('longitude', lng, { shouldDirty: true });
                  }}
                />
              )}
            />
          </FormSection>

          {/* ── Seção 5 — Dados eleitorais ── */}
          <FormSection title="Dados eleitorais">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <FieldGroup label="Título de eleitor" className="sm:col-span-3">
                <input {...register('voter_registration')} placeholder="0000 0000 0000" className={inputCls(false)} />
              </FieldGroup>
              <FieldGroup label="Zona eleitoral">
                <input {...register('electoral_zone')} placeholder="001" className={inputCls(false)} />
              </FieldGroup>
              <FieldGroup label="Seção eleitoral">
                <input {...register('electoral_section')} placeholder="0001" className={inputCls(false)} />
              </FieldGroup>
              <FieldGroup label="Engajamento (0–100)">
                <input
                  {...register('engagement_score')}
                  type="number" min={0} max={100}
                  placeholder="0"
                  className={inputCls(!!errors.engagement_score)}
                />
              </FieldGroup>
            </div>
          </FormSection>

          {/* ── Seção 5 — Notas ── */}
          <FormSection title="Observações">
            <textarea
              {...register('notes')}
              rows={3}
              placeholder="Anotações internas sobre este contato…"
              className={`${inputCls(false)} resize-none`}
            />
          </FormSection>

        </form>
          </div>

          {/* Footer — fixo na base */}
          <div className="flex justify-end gap-3 px-6 py-4 hairline-t glass-strong shrink-0 rounded-b-2xl">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl btn-ghost px-5 py-2.5 text-sm font-semibold cursor-pointer"
            >
              Cancelar
            </button>
            <button
              form="contact-form"
              type="submit"
              disabled={isPending}
              className="flex items-center gap-2 rounded-xl btn-accent px-5 py-2.5 text-sm cursor-pointer disabled:opacity-60"
            >
              {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              <span>{contact ? 'Salvar alterações' : 'Criar contato'}</span>
            </button>
          </div>

        </div>
    </div>,
    document.body
  );
}

/* ─── Helpers de UI ─── */

function inputCls(hasError: boolean) {
  return `w-full rounded-xl bg-midnight-2/60 border ${
    hasError ? 'border-danger/60' : 'border-hairline'
  } px-3 py-2.5 text-sm text-ink placeholder-ink-faint
  focus:outline-none focus:border-accent/50 focus:bg-midnight-2 transition-all
  [&_option]:bg-midnight-3 [&_option]:text-ink`;
}

interface FieldGroupProps {
  label: string;
  error?: string;
  className?: string;
  children: React.ReactNode;
}

function FieldGroup({ label, error, className = '', children }: FieldGroupProps) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <label className="font-mono text-[10px] uppercase tracking-[0.18em] font-semibold text-ink-muted">
        {label}
      </label>
      {children}
      {error && <p className="text-[11px] text-danger">{error}</p>}
    </div>
  );
}

function FormSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-4">
      <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] font-semibold text-ink-subtle flex items-center gap-2">
        <span className="flex-grow hairline-b pb-1">{title}</span>
      </h3>
      {children}
    </div>
  );
}
